import React, {useState} from "react";
import { Formik } from "formik";
import {
    TextField,
    Paper,
    withStyles,
    Button,
    Typography,
    Chip,
    CircularProgress, Tooltip, IconButton, FormGroup, FormControlLabel, Checkbox,
} from "@material-ui/core";
import * as Yup from "yup";
import Autocomplete from '@material-ui/lab/Autocomplete';
import {BackButton, hasPermission} from '../../sc-utilities';
import {Info} from "@material-ui/icons";
import LicenseEditModal from "./LicenseEditModal";
import DisableLicensingModal from "./DisableLicensingModal";
import {getUnitNameTranslation, shouldDisableEnableLicensingCheckbox} from "../../sc-users/User/UserDetails/UserEdit";

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(1),
        margin: theme.spacing(1)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    },
    licensing: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        display: "flex",
        alignItems: "center",
    },
    icon: {
        color: 'rgb(220,220,220)',
    },
    assignedLicensesButton: {
        marginLeft: theme.spacing(),
    }
});

const GroupForm = props => {
    const { classes, formSettings, groups=[], users=[], initialValues, handleSubmit, translations, activeCustomerID, onEditGroupLicenses, onLoadGroup, isEdit, permissions, group, activeCustomerGroupLicensingSetting } = props;

    const [licenseModalState, setLicenseModalState] = useState({open: false});

    const [disableLicensingModalState, setDisableLicensingModalState] = useState({open: false});

    const closeLicenseEditModal = () => {
        setLicenseModalState(prevState => {return {...prevState, open: false}});
    }

    const saveLicenseEditModal = async (Balances) => {
        setLicenseModalState({open: false});
        await onEditGroupLicenses({GroupID: group?.GroupID, ActiveCustomerID: activeCustomerID, Balances, GroupLicensingEnabled: true});
        await onLoadGroup({ GroupID: group?.GroupID, ActiveCustomerID: activeCustomerID });
    }

    const handleCloseDisableLicensingModal = () => {
        setDisableLicensingModalState({open: false});
    }

    const handleSaveDisableLicensingModal = async () => {
        setLicenseModalState(prevState => {return {...prevState, GroupLicensingEnabled: false}});
        setDisableLicensingModalState({open: false});

        await onEditGroupLicenses({GroupID: group?.GroupID, ActiveCustomerID: activeCustomerID, Balances: [], GroupLicensingEnabled: false});
        await onLoadGroup({ GroupID: group?.GroupID, ActiveCustomerID: activeCustomerID });
    }

    const disabledEnableLicensingCheckbox = shouldDisableEnableLicensingCheckbox(activeCustomerGroupLicensingSetting);

    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize={true}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, setFieldValue, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, initialValues } = formikProps;

                    const tooltip = <Typography variant="subtitle1">
                        {values.Balances?.length>0 && values.Balances?.map((balance) => <React.Fragment key={balance.Unit}><span>{getUnitNameTranslation(balance.Unit, translations)}: {balance.Unit==="currency"? balance.Credits : Number(balance.Credits)}</span><br/></React.Fragment>)}
                    </Typography>

                    const handleEnableLicensingChange = (e) => {
                        const checked = e.target.checked;
                        const alreadyDisabled = !checked && !initialValues.GroupLicensingEnabled;
                        const shouldOpenModal = alreadyDisabled? false : !checked;
                        setDisableLicensingModalState({open: shouldOpenModal});
                        setFieldValue("GroupLicensingEnabled", !shouldOpenModal? checked : !!(values.GroupLicensingEnabled));
                    }

                    const balanceEdit = <div className={classes.licensing}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="checkbox_licensing"
                                        name={"GroupLicensingEnabled"}
                                        checked={!!(values.GroupLicensingEnabled)}
                                        onChange={(e) => handleEnableLicensingChange(e)}
                                        color={'primary'}
                                        disabled={disabledEnableLicensingCheckbox}
                                    />
                                }
                                label={translations["enable_group_licensing"] || "Enable group licensing"}
                            />
                            {disabledEnableLicensingCheckbox && <Typography variant="subtitle2">({translations['checkbox_licensing_setting_disabled_message'] || "Checkbox disabled due to customer setting"})</Typography>}
                        </FormGroup>

                        {values.GroupLicensingEnabled &&
                        <>
                            <Button variant="contained" onClick={() => setLicenseModalState({open: true})} className={classes.assignedLicensesButton}>{translations['assigned_licenses'] || "Assigned licenses"}</Button>
                            <Tooltip title={values.Balances && Object.keys(values.Balances).length>0? tooltip : (translations["no_licenses"] || "No licenses")}>
                                <IconButton aria-haspopup="true" color="inherit">
                                    <Info className={classes.icon} />
                                </IconButton>
                            </Tooltip>
                            <LicenseEditModal group={group} modalState={licenseModalState} translations={translations} handleClose={closeLicenseEditModal} handleSave={saveLicenseEditModal} balances={values.Balances} currentBalances={initialValues.Balances || []} />
                        </>}
                        <DisableLicensingModal modalState={disableLicensingModalState} handleClose={handleCloseDisableLicensingModal} handleSave={handleSaveDisableLicensingModal} title={translations['disable_group_licensing'] || 'Disable group licensing'} translations={translations} />
                    </div>

                    const hasAccessAllGroupsPermission = hasPermission(permissions, 'access_all_groups');

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                             {isEdit && hasPermission(permissions, 'update_group_balance')? balanceEdit : null}

                            <TextField
                                name="Name"
                                value={values.Name || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations['name'] || 'Name'}
                                label={translations['name'] || 'Name'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <Autocomplete
                                name="Parent"
                                id="ParentIDSelect"
                                options={hasAccessAllGroupsPermission? [{GroupID: null, Name: translations['none'] || "None"}, ...groups] || [] : groups || []}
                                getOptionLabel={(option) => option.Name || ""}
                                getOptionSelected={(option, value) => option.GroupID === value.GroupID}
                                filterSelectedOptions
                                value={values.Parent || ""}
                                onChange={(_, value) => { setFieldValue('Parent', value) }}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField {...params} variant="standard" fullWidth={true} label={`${translations["choose_parent"] || "Choose parent"}`} />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option.Name} {...getTagProps({ index })} />
                                    ))
                                }
                            />
                            <Autocomplete
                                name="Users"
                                multiple
                                id="UsersMultiSelect"
                                options={users || []}
                                getOptionLabel={(option) => option.displayValue || ""}
                                filterSelectedOptions
                                getOptionSelected={(option, value) => option.value === value.value}
                                value={values.Users || ""}
                                onChange={(_, value) => setFieldValue('Users', value)}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField {...params} variant="standard" fullWidth={true} label={`${translations["choose_group_users"] || "Choose group users"}`} />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option.displayValue} {...getTagProps({ index })} />
                                    ))
                                }
                            />
                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        fullWidth={true}
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};
export default withStyles(styles, { withTheme: true })(GroupForm);
